import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Info, TrendingUp, TrendingDown, Calculator } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { StockItem } from "@/types/stock-item";
import { StockItemCostingInfo } from "@/components/stock-item-costing-info";

interface StockItemPricingTabProps {
  item: StockItem;
  isEditMode: boolean;
  onUpdate?: (updates: Partial<StockItem>) => void;
  onItemChange?: (item: StockItem) => void;
  validationErrors?: Record<string, string>;
  touchedFields?: Set<string>;
  onFieldBlur?: (field: keyof StockItem) => void;
}

export function StockItemPricingTab({ item, isEditMode, onUpdate, onItemChange, validationErrors, touchedFields, onFieldBlur }: StockItemPricingTabProps) {
  const [localUnitPrice, setLocalUnitPrice] = useState(item.unitPrice);
  const [localLastCostPrice, setLocalLastCostPrice] = useState(item.lastCostPrice);
  const [showCostWarning, setShowCostWarning] = useState(false);

  // Sync local state with item props when item changes from parent
  useEffect(() => {
    setLocalUnitPrice(item.unitPrice);
    setLocalLastCostPrice(item.lastCostPrice);
  }, [item]);

  // Format currency with thousand separators
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format percentage with 1 decimal place
  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  // Calculate gross margin percentage
  const grossMarginPercentage = useMemo(() => {
    if (localUnitPrice === 0) return 0;
    return ((localUnitPrice - item.averageCost) / localUnitPrice) * 100;
  }, [localUnitPrice, item.averageCost]);

  // Calculate margin value per unit
  const marginValue = useMemo(() => {
    return localUnitPrice - item.averageCost;
  }, [localUnitPrice, item.averageCost]);

  // Calculate current stock value
  const currentStockValue = useMemo(() => {
    return item.physicalQuantity * localUnitPrice;
  }, [item.physicalQuantity, localUnitPrice]);

  // Calculate value at cost
  const valueAtCost = useMemo(() => {
    return item.physicalQuantity * item.averageCost;
  }, [item.physicalQuantity, item.averageCost]);

  // Calculate lifetime margin
  const lifetimeMarginPercentage = useMemo(() => {
    if (item.totalValueOfSales === 0) return 0;
    return ((item.totalValueOfSales - item.totalCostOfSales) / item.totalValueOfSales) * 100;
  }, [item.totalValueOfSales, item.totalCostOfSales]);

  // Get color for margin percentage
  const getMarginColor = (percentage: number): string => {
    if (percentage > 30) return "text-green-600";
    if (percentage >= 15) return "text-orange-600";
    return "text-red-600";
  };

  // Get background color for margin card
  const getMarginBgColor = (percentage: number): string => {
    if (percentage > 30) return "bg-green-50 border-green-200";
    if (percentage >= 15) return "bg-orange-50 border-orange-200";
    return "bg-red-50 border-red-200";
  };

  // Handle unit price change
  const handleUnitPriceChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    setLocalUnitPrice(numValue);
    
    // Show warning if unit price is less than average cost
    if (numValue < item.averageCost && numValue > 0) {
      setShowCostWarning(true);
    } else {
      setShowCostWarning(false);
    }

    if (isEditMode) {
      // Use onItemChange if available (for create mode), otherwise use onUpdate (for edit mode)
      if (onItemChange) {
        onItemChange({ ...item, unitPrice: numValue });
      } else if (onUpdate) {
        onUpdate({ unitPrice: numValue });
      }
    }
  };

  // Handle last cost price change
  const handleLastCostPriceChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    setLocalLastCostPrice(numValue);
    
    if (isEditMode) {
      // Use onItemChange if available (for create mode), otherwise use onUpdate (for edit mode)
      if (onItemChange) {
        onItemChange({ ...item, lastCostPrice: numValue });
      } else if (onUpdate) {
        onUpdate({ lastCostPrice: numValue });
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Costing Information Card */}
      <StockItemCostingInfo item={item} />

      {/* Current Pricing Card */}
      <Card>
        <CardHeader>
          <CardTitle>Current Pricing</CardTitle>
          <CardDescription>Manage item pricing and cost information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Unit Price */}
            <div className="space-y-2">
              <Label htmlFor="unitPrice">Unit Price</Label>
              {isEditMode ? (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                  <Input
                    id="unitPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={localUnitPrice.toFixed(2)}
                    onChange={(e) => handleUnitPriceChange(e.target.value)}
                    onBlur={() => onFieldBlur?.("unitPrice")}
                    className={`pl-7 ${validationErrors?.unitPrice && touchedFields?.has("unitPrice") ? "border-destructive" : ""}`}
                  />
                </div>
              ) : (
                <div className="text-2xl font-semibold">{formatCurrency(localUnitPrice)}</div>
              )}
              {validationErrors?.unitPrice && touchedFields?.has("unitPrice") && (
                <p className="text-sm text-destructive">{validationErrors.unitPrice}</p>
              )}
            </div>

            {/* Last Cost Price */}
            <div className="space-y-2">
              <Label htmlFor="lastCostPrice">Last Cost Price</Label>
              {isEditMode ? (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                  <Input
                    id="lastCostPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={localLastCostPrice.toFixed(2)}
                    onChange={(e) => handleLastCostPriceChange(e.target.value)}
                    onBlur={() => onFieldBlur?.("lastCostPrice")}
                    className={`pl-7 ${validationErrors?.lastCostPrice && touchedFields?.has("lastCostPrice") ? "border-destructive" : ""}`}
                  />
                </div>
              ) : (
                <div className="text-2xl font-semibold">{formatCurrency(localLastCostPrice)}</div>
              )}
              {validationErrors?.lastCostPrice && touchedFields?.has("lastCostPrice") && (
                <p className="text-sm text-destructive">{validationErrors.lastCostPrice}</p>
              )}
              <p className="text-xs text-muted-foreground">Most recent purchase cost</p>
            </div>

            {/* Average Cost */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>Average Cost</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Calculator className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Calculated from purchase history</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-2xl font-semibold text-muted-foreground">{formatCurrency(item.averageCost)}</div>
              <p className="text-xs text-muted-foreground">Average cost across all purchases</p>
            </div>
          </div>

          {/* Cost Warning */}
          {showCostWarning && (
            <Alert className="mt-4 border-orange-200 bg-orange-50">
              <Info className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                Warning: Unit price is lower than average cost. This will result in a negative margin.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Margin Analysis Card */}
      <Card className={getMarginBgColor(grossMarginPercentage)}>
        <CardHeader>
          <CardTitle>Margin Analysis</CardTitle>
          <CardDescription>Profitability metrics for this item</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Gross Margin Percentage */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>Gross Margin</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Formula: ((Unit Price - Average Cost) / Unit Price) × 100</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-2">
                <div className={`text-4xl font-bold ${getMarginColor(grossMarginPercentage)}`}>
                  {formatPercentage(grossMarginPercentage)}
                </div>
                {grossMarginPercentage > 0 ? (
                  <TrendingUp className={`h-6 w-6 ${getMarginColor(grossMarginPercentage)}`} />
                ) : (
                  <TrendingDown className="h-6 w-6 text-red-600" />
                )}
              </div>
            </div>

            {/* Margin Value */}
            <div className="space-y-2">
              <Label>Margin per Unit</Label>
              <div className={`text-4xl font-bold ${marginValue >= 0 ? getMarginColor(grossMarginPercentage) : 'text-red-600'}`}>
                {formatCurrency(marginValue)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stock Value Card */}
      <Card>
        <CardHeader>
          <CardTitle>Stock Value</CardTitle>
          <CardDescription>Total value of current inventory</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current Stock Value */}
            <div className="space-y-2">
              <Label>Current Stock Value</Label>
              <div className="text-3xl font-bold text-primary">{formatCurrency(currentStockValue)}</div>
              <p className="text-xs text-muted-foreground">
                {item.physicalQuantity} units × {formatCurrency(localUnitPrice)}
              </p>
            </div>

            {/* Value at Cost */}
            <div className="space-y-2">
              <Label>Value at Cost</Label>
              <div className="text-3xl font-bold text-muted-foreground">{formatCurrency(valueAtCost)}</div>
              <p className="text-xs text-muted-foreground">
                {item.physicalQuantity} units × {formatCurrency(item.averageCost)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Historical Performance Card */}
      <Card>
        <CardHeader>
          <CardTitle>Historical Performance</CardTitle>
          <CardDescription>Lifetime sales and cost metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Total Cost of Sales */}
              <div className="space-y-2">
                <Label>Total Cost of Sales</Label>
                <div className="text-2xl font-semibold">{formatCurrency(item.totalCostOfSales)}</div>
                <p className="text-xs text-muted-foreground">Lifetime cost of goods sold</p>
              </div>

              {/* Total Value of Sales */}
              <div className="space-y-2">
                <Label>Total Value of Sales</Label>
                <div className="text-2xl font-semibold">{formatCurrency(item.totalValueOfSales)}</div>
                <p className="text-xs text-muted-foreground">Lifetime revenue generated</p>
              </div>
            </div>

            {/* Lifetime Margin */}
            <div className="space-y-2 pt-4 border-t">
              <Label>Lifetime Margin</Label>
              <div className="flex items-center gap-3">
                <div className={`text-3xl font-bold ${getMarginColor(lifetimeMarginPercentage)}`}>
                  {formatPercentage(lifetimeMarginPercentage)}
                </div>
                {lifetimeMarginPercentage > 0 ? (
                  <TrendingUp className={`h-6 w-6 ${getMarginColor(lifetimeMarginPercentage)}`} />
                ) : (
                  <TrendingDown className="h-6 w-6 text-red-600" />
                )}
                <div className="text-lg text-muted-foreground">
                  ({formatCurrency(item.totalValueOfSales - item.totalCostOfSales)} profit)
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
